import * as THREE from 'three';
import { loadGlb } from '../../../assets/loadGlb';
import { DinoActor, type FaunaSense } from '../../../dinos/DinoActor';
import { ANKY_KIT, CARNO_KIT } from '../../../dinos/kits';
import type { Xyz } from '../../../interact/Interact';
import type { PhysicsWorld } from '../../../physics/PhysicsWorld';

export type VolcanicPoint = { x: number; y: number; z: number };

export type VolcanicHook = {
  dinos?: { id: string; x: number; y: number; z: number; hp: number; kind: string; taxon: string }[];
};

/** Dressing streams in once the player is north of Deep Jungle. */
export const VZ_STREAM_Z = -160;
/** Area slug flips past the jungle fringe, not on the ridge. */
export const VZ_AREA_Z = -170;

/** Heat flag: thirst drain × this while the slug is volcanic. */
export const VZ_HEAT_MULT = 1.8;

export const VZ_HUT: VolcanicPoint = { x: 6, y: 1, z: -188 };

export const VZ_NODES: Record<string, VolcanicPoint> = {
  'VZ-HUT': VZ_HUT,
  'VZ-LAVA': { x: -8, y: 0.06, z: -176 },
  'VZ-CINDER': { x: -4, y: 0.4, z: -196 },
  'VZ-CARNO': { x: 16, y: 1, z: -200 },
  'VZ-ANKY': { x: -12, y: 1, z: -172 },
};

export const VZ_FOG_COLOR = 0x5a4034;
export const VZ_FOG_DENSITY = 0.013;
export const VZ_FOG_NIGHT = 0.02;

const GROUND_HALF_Y = 0.5;

type VolcMats = {
  ash: THREE.MeshStandardMaterial;
  silt: THREE.MeshStandardMaterial;
  lava: THREE.MeshStandardMaterial;
  crust: THREE.MeshStandardMaterial;
  rock: THREE.MeshStandardMaterial;
  station: THREE.MeshStandardMaterial;
  roof: THREE.MeshStandardMaterial;
  rust: THREE.MeshStandardMaterial;
  soot: THREE.MeshStandardMaterial;
};

function loadMap(url: string, rx: number, ry: number): THREE.Texture {
  const tex = new THREE.TextureLoader().load(url);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.repeat.set(rx, ry);
  tex.anisotropy = 8;
  return tex;
}

function makeMats(): VolcMats {
  return {
    ash: new THREE.MeshStandardMaterial({
      color: 0x3a342c,
      map: loadMap('/tex/rock.jpg', 5, 6),
      roughness: 0.98,
      metalness: 0.04,
    }),
    silt: new THREE.MeshStandardMaterial({
      color: 0x2a241c,
      map: loadMap('/tex/sand.jpg', 6, 7),
      roughness: 0.96,
      metalness: 0.03,
    }),
    lava: new THREE.MeshStandardMaterial({
      color: 0x2a1008,
      emissive: 0xff3a08,
      emissiveIntensity: 1.15,
      roughness: 0.42,
      metalness: 0.08,
    }),
    crust: new THREE.MeshStandardMaterial({
      color: 0x1a100c,
      map: loadMap('/tex/rock.jpg', 3, 2),
      emissive: 0x4a1408,
      emissiveIntensity: 0.35,
      roughness: 0.7,
      metalness: 0.08,
    }),
    rock: new THREE.MeshStandardMaterial({
      color: 0x2c2824,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.96,
      metalness: 0.05,
    }),
    station: new THREE.MeshStandardMaterial({
      color: 0x5a5248,
      map: loadMap('/tex/rock.jpg', 2, 2),
      roughness: 0.9,
      metalness: 0.1,
    }),
    roof: new THREE.MeshStandardMaterial({
      color: 0x2a221c,
      roughness: 0.84,
      metalness: 0.16,
    }),
    rust: new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      map: loadMap('/tex/wreck.jpg', 1, 1),
      roughness: 0.7,
      metalness: 0.28,
    }),
    soot: new THREE.MeshStandardMaterial({
      color: 0x1c1814,
      roughness: 0.95,
      metalness: 0.04,
    }),
  };
}

export function heatAt(z: number): boolean {
  return z < VZ_AREA_Z;
}

/**
 * Volcanic fringe north of Deep Jungle. Coast / jungle stay resident;
 * this dressing streams in at z < -160 and the area slug flips at z < -170.
 */
const CARNO_BOUNDS = { minX: 6, maxX: 24, minZ: -212, maxZ: -188 };
const ANKY_BOUNDS = { minX: -22, maxX: -2, minZ: -182, maxZ: -164 };

export class VolcanicZone {
  streamed = false;
  /** True once the player is inside the volcanic slug. */
  heat = false;
  carno: DinoActor | null = null;
  anky: DinoActor | null = null;
  private groundReady = false;

  extendGround(physics: PhysicsWorld): void {
    if (this.groundReady) {
      return;
    }
    // Jungle extra slab ends at z ≈ -140. Cover the ash shelf through Station Cinder.
    physics.addStaticCuboid(32, GROUND_HALF_Y, 42, 0, -GROUND_HALF_Y, -182);
    this.groundReady = true;
  }

  /** Load dressing the first time the player is north of the jungle. */
  sync(scene: THREE.Scene, physics: PhysicsWorld, z: number): void {
    this.extendGround(physics);
    this.heat = heatAt(z);
    if (this.streamed || z >= VZ_STREAM_Z) {
      return;
    }
    this.build(scene, physics);
  }

  tick(dt: number, player: Xyz, sense?: FaunaSense): void {
    this.carno?.tick(dt, player, sense);
    this.anky?.tick(dt, player, sense);
  }

  /** BURN lock: ignite Mount Cinder's Meridian charge. */
  tryIgnite(player: Xyz): boolean {
    const at = VZ_NODES['VZ-CINDER'] ?? { x: -4, y: 0.4, z: -196 };
    return Math.hypot(player.x - at.x, player.z - at.z) <= 3.4;
  }

  publish(hook: VolcanicHook): void {
    const extra = [this.carno, this.anky].filter((dino): dino is DinoActor => dino !== null);
    if (extra.length > 0) {
      hook.dinos = [...(hook.dinos ?? []), ...extra.map((dino) => dino.snap())];
    }
  }

  applyFog(scene: THREE.Scene, night: boolean): void {
    if (scene.fog instanceof THREE.FogExp2) {
      scene.fog.density = night ? VZ_FOG_NIGHT : VZ_FOG_DENSITY;
      scene.fog.color.setHex(VZ_FOG_COLOR);
    }
    if (scene.background instanceof THREE.Color) {
      scene.background.setHex(night ? 0x1a1410 : VZ_FOG_COLOR);
    }
  }

  private build(scene: THREE.Scene, physics: PhysicsWorld): void {
    this.streamed = true;
    const root = new THREE.Group();
    tag(root, 'Volcanic Zone');
    root.userData.area = 'volcanic';
    scene.add(root);

    const mats = makeMats();
    addAsh(root, mats);
    addAshDunes(root, mats);
    addLava(root, mats);
    addBasalt(root, physics, mats);
    addCinders(root, physics, mats);
    addStumps(root, physics, mats);
    addHut(root, physics, mats);
    this.spawnHunters(root);
  }

  private spawnHunters(root: THREE.Group): void {
    const carnoSpot = VZ_NODES['VZ-CARNO'] ?? { x: 16, y: 1, z: -200 };
    this.carno = new DinoActor('VZ-CARNO', CARNO_KIT, carnoSpot.x, carnoSpot.z, CARNO_BOUNDS);
    root.add(this.carno.mesh);
    const ankySpot = VZ_NODES['VZ-ANKY'] ?? { x: -12, y: 1, z: -172 };
    this.anky = new DinoActor('VZ-ANKY', ANKY_KIT, ankySpot.x, ankySpot.z, ANKY_BOUNDS);
    root.add(this.anky.mesh);
  }
}

function addAsh(root: THREE.Group, mats: VolcMats): void {
  const geo = new THREE.PlaneGeometry(68, 84, 18, 22);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  if (pos) {
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i) - 186;
      let y = Math.sin(x * 0.09 + z * 0.07) * 0.06 + Math.sin(x * 0.28) * 0.03;
      if (Math.hypot(x, z + 180) < 5 || Math.hypot(x - 6, z + 188) < 4) {
        y = 0;
      }
      pos.setY(i, 0.02 + y);
    }
    pos.needsUpdate = true;
  }
  geo.computeVertexNormals();
  const floor = new THREE.Mesh(geo, mats.ash);
  floor.position.set(0, 0, -186);
  floor.receiveShadow = true;
  tag(floor, 'VZ-ASH');
  root.add(floor);
}

function addAshDunes(root: THREE.Group, mats: VolcMats): void {
  const dunes: [number, number, number][] = [
    [-16, -168, 5.4],
    [18, -172, 4.8],
    [-22, -186, 6.2],
    [20, -194, 5.1],
    [-18, -204, 5.6],
    [14, -210, 4.6],
    [-8, -164, 3.8],
    [10, -202, 4.2],
  ];
  for (let i = 0; i < dunes.length; i++) {
    const d = dunes[i];
    if (!d) {
      continue;
    }
    const [x, z, r] = d;
    const disk = new THREE.Mesh(new THREE.CircleGeometry(r, 14), mats.silt);
    disk.rotation.x = -Math.PI / 2 + (i % 2 === 0 ? 0.04 : -0.03);
    disk.position.set(x, 0.04, z);
    disk.receiveShadow = true;
    tag(disk, `VZ-DUNE-${String(i + 1).padStart(2, '0')}`);
    root.add(disk);
  }
}

function addLava(root: THREE.Group, mats: VolcMats): void {
  const pos = VZ_NODES['VZ-LAVA'] ?? { x: -8, y: 0.06, z: -176 };
  const cracks: [number, number, number, number][] = [
    [pos.x, pos.z, 9.2, 0.55],
    [pos.x + 4.5, pos.z - 8, 6.4, 0.4],
    [pos.x - 6, pos.z - 14, 7.1, 0.42],
    [2, -200, 5.2, 0.36],
    [8, -184, 6.0, 0.38],
    [-14, -198, 5.6, 0.34],
    [4, -210, 4.8, 0.32],
  ];
  for (let i = 0; i < cracks.length; i++) {
    const c = cracks[i];
    if (!c) {
      continue;
    }
    const [x, z, len, w] = c;
    const strip = new THREE.Mesh(new THREE.PlaneGeometry(len, w), mats.lava);
    strip.rotation.x = -Math.PI / 2;
    strip.rotation.z = i * 0.4 - 0.2;
    strip.position.set(x, pos.y, z);
    tag(strip, i === 0 ? 'VZ-LAVA' : `VZ-LAVA-${String(i + 1).padStart(2, '0')}`);
    root.add(strip);
  }

  const glow = new THREE.PointLight(0xff5a18, 1.6, 28, 1.6);
  glow.position.set(pos.x, 1.4, pos.z);
  glow.name = 'VZ-LAVA-GLOW';
  root.add(glow);

  const glow2 = new THREE.PointLight(0xff3a10, 0.9, 18, 1.8);
  glow2.position.set(pos.x + 4.5, 1.1, pos.z - 8);
  glow2.name = 'VZ-LAVA-GLOW-2';
  root.add(glow2);

  const crusts: [number, number, number][] = [
    [pos.x + 1.2, pos.z + 1.4, 1.6],
    [pos.x - 2.4, pos.z - 3.2, 1.3],
    [pos.x + 3.6, pos.z - 9.2, 1.4],
    [-12, -196, 1.5],
  ];
  for (let i = 0; i < crusts.length; i++) {
    const c = crusts[i];
    if (!c) {
      continue;
    }
    const plate = new THREE.Mesh(new THREE.CircleGeometry(c[2], 8), mats.crust);
    plate.rotation.x = -Math.PI / 2;
    plate.position.set(c[0], pos.y + 0.012, c[1]);
    tag(plate, `VZ-CRUST-${String(i + 1).padStart(2, '0')}`);
    root.add(plate);
  }
}

function addBasalt(root: THREE.Group, physics: PhysicsWorld, mats: VolcMats): void {
  const cols: [number, number, number, number][] = [
    [-22, -170, 1.8, 0.38],
    [-24, -174, 2.4, 0.32],
    [-21, -176, 1.5, 0.28],
    [22, -172, 2.1, 0.36],
    [24, -176, 2.7, 0.3],
    [21, -180, 1.6, 0.26],
    [-24, -200, 2.2, 0.34],
    [-22, -204, 1.7, 0.28],
    [22, -206, 2.5, 0.32],
    [24, -210, 1.9, 0.28],
  ];
  for (let i = 0; i < cols.length; i++) {
    const c = cols[i];
    if (!c) {
      continue;
    }
    const [x, z, h, r] = c;
    const col = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 1.08, h, 6), mats.rock);
    col.position.set(x, h * 0.5, z);
    col.rotation.y = i * 0.3;
    col.castShadow = true;
    col.receiveShadow = true;
    tag(col, `VZ-BASALT-${String(i + 1).padStart(2, '0')}`);
    root.add(col);
    physics.addStaticCylinder(h * 0.5, r, x, h * 0.5, z);
  }
}

function addCinders(root: THREE.Group, physics: PhysicsWorld, mats: VolcMats): void {
  const specs: [number, number, number][] = [
    [-14, -172, 1.1],
    [12, -178, 0.85],
    [-18, -192, 1.35],
    [16, -198, 1.05],
    [-10, -208, 0.95],
    [8, -168, 0.7],
    [-16, -180, 0.9],
    [14, -186, 0.8],
    [-12, -214, 1.15],
    [18, -208, 0.75],
  ];
  for (let i = 0; i < specs.length; i++) {
    const s = specs[i];
    if (!s) {
      continue;
    }
    const [x, z, r] = s;
    const rock = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mats.rock);
    rock.position.set(x, r * 0.45, z);
    rock.rotation.set(0.2, x * 0.15, 0.1);
    rock.castShadow = true;
    rock.receiveShadow = true;
    tag(rock, `VZ-ROCK-${String(i + 1).padStart(2, '0')}`);
    root.add(rock);
    physics.addStaticCuboid(r * 0.55, r * 0.4, r * 0.55, x, r * 0.45, z);
  }
}

function addStumps(root: THREE.Group, physics: PhysicsWorld, mats: VolcMats): void {
  const spots: [number, number][] = [
    [-20, -184],
    [18, -190],
    [-6, -210],
    [10, -174],
  ];
  for (let i = 0; i < spots.length; i++) {
    const s = spots[i];
    if (!s) {
      continue;
    }
    const [x, z] = s;
    const stump = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.3, 0.7, 6), mats.soot);
    stump.position.set(x, 0.35, z);
    stump.castShadow = true;
    tag(stump, `VZ-STUMP-${String(i + 1).padStart(2, '0')}`);
    root.add(stump);
    physics.addStaticCylinder(0.35, 0.28, x, 0.35, z);
  }
}

function addHut(root: THREE.Group, physics: PhysicsWorld, mats: VolcMats): void {
  const hut = new THREE.Group();
  tag(hut, 'Station Cinder');
  hut.position.set(VZ_HUT.x, 0, VZ_HUT.z);
  root.add(hut);

  const body = new THREE.Mesh(new THREE.BoxGeometry(4.6, 2.6, 3.6), mats.station);
  body.position.y = 1.3;
  body.castShadow = true;
  body.receiveShadow = true;
  tag(body, 'VZ-HUT');
  hut.add(body);

  const roof = new THREE.Mesh(new THREE.BoxGeometry(5.1, 0.16, 4.1), mats.roof);
  roof.position.y = 2.68;
  roof.castShadow = true;
  hut.add(roof);

  const plaque = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.26, 0.06), mats.rust);
  plaque.position.set(0, 2.1, 1.84);
  hut.add(plaque);

  const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 1.4, 7), mats.rust);
  stack.position.set(1.4, 3.3, -0.4);
  hut.add(stack);

  const coal = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 0), mats.lava);
  coal.position.set(-1.6, 0.18, 1.6);
  coal.scale.set(1.4, 0.5, 1.1);
  hut.add(coal);

  physics.addStaticCuboid(2.3, 1.3, 1.8, VZ_HUT.x, 1.3, VZ_HUT.z);
  void dressCinderHut(hut, mats);
}

const HUT_GLB = '/models/bld/bld_cinder_hut.glb';

/** Swap the box hut for the authored Station Cinder GLB. Keeps primitives if the file is missing. */
async function dressCinderHut(hut: THREE.Group, mats: VolcMats): Promise<void> {
  try {
    const hero = await loadGlb(HUT_GLB);
    hero.name = 'VZ-HUT-HERO';
    const rock = mats.station.map;
    const wreck = mats.rust.map;
    hero.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) {
        return;
      }
      obj.castShadow = true;
      obj.receiveShadow = true;
      const slots = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of slots) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) {
          continue;
        }
        const slug = mat.name.toLowerCase();
        if (rock && (slug.includes('concrete') || slug.includes('soot'))) {
          mat.map = rock;
          mat.needsUpdate = true;
        }
        if (wreck && (slug.includes('rust') || slug.includes('roof') || slug.includes('enamel'))) {
          mat.map = wreck;
          mat.needsUpdate = true;
        }
        if (slug.includes('lava')) {
          mat.emissive.setHex(0xff3a08);
          mat.emissiveIntensity = Math.max(mat.emissiveIntensity, 1.15);
          mat.needsUpdate = true;
        }
      }
    });
    for (const child of [...hut.children]) {
      child.visible = false;
    }
    hut.add(hero);
  } catch {
    /* keep box hut */
  }
}

function tag(obj: THREE.Object3D, id: string): void {
  obj.name = id;
  obj.userData.id = id;
}
